import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { CATEGORIES, TAGS, getCategoryColor, getTagColor, getStatusColor } from '../utils/constants';
import supabase from '../lib/supabase';

const { FiArrowLeft, FiSearch, FiBookOpen, FiHelpCircle, FiLoader } = FiIcons;

const KnowledgeBase = ({ onNavigate }) => {
  const [activeTab, setActiveTab] = useState('answered');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [learnings, setLearnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchKnowledgeBaseData();
  }, []);

  const fetchKnowledgeBaseData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch answered questions
      const { data: questionsData, error: questionsError } = await supabase
        .from('questions_kb2024')
        .select('id, title, category, status, tags, asked_by, created_at, description')
        .eq('status', 'Answered')
        .order('created_at', { ascending: false });

      if (questionsError) throw questionsError;

      // Fetch learnings
      const { data: learningsData, error: learningsError } = await supabase
        .from('learnings_kb2024')
        .select('id, title, category, tags, shared_by, created_at, content')
        .order('created_at', { ascending: false });

      if (learningsError) throw learningsError;

      setAnsweredQuestions(questionsData || []);
      setLearnings(learningsData || []);
    } catch (error) {
      console.error('Error fetching knowledge base data:', error);
      setError('Unable to load knowledge base. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on active tab and filters
  const getFilteredData = () => {
    const data = activeTab === 'answered' ? answeredQuestions : learnings;
    return data.filter(item => {
      const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (activeTab === 'answered' 
          ? item.description?.toLowerCase().includes(searchTerm.toLowerCase())
          : item.content?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      const matchesCategory = !categoryFilter || item.category === categoryFilter;
      const matchesTag = !tagFilter || (item.tags && item.tags.includes(tagFilter));
      return matchesSearch && matchesCategory && matchesTag;
    });
  };

  const filteredData = getFilteredData();

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const truncateText = (text, maxLength = 200) => {
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiLoader} className="w-8 h-8 text-purple-600 animate-spin" />
            <span className="text-lg text-gray-600">Loading knowledge base...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <button
          onClick={() => onNavigate('dashboard')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors duration-200"
        >
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
          Back to Dashboard
        </button>
        <div className="flex items-center gap-3 mb-2">
          <SafeIcon icon={FiBookOpen} className="w-8 h-8 text-purple-600" />
          <h1 className="text-3xl font-bold text-gray-900">Knowledge Base</h1>
        </div>
        <p className="text-gray-600">Browse answered questions and community learnings</p>
      </motion.div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6"
        >
          <p className="text-red-700">{error}</p>
          <button
            onClick={fetchKnowledgeBaseData}
            className="mt-2 text-red-600 hover:text-red-700 font-medium"
          >
            Try Again
          </button>
        </motion.div>
      )}

      {/* Search and Filter */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6"
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div>
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search questions and learnings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
          <div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">All Categories</option>
              {CATEGORIES.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
          <div>
            <select
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">All Tags</option>
              {TAGS.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          </div>
        </div>
      </motion.div>

      {/* Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8"
      >
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('answered')}
            className={`flex items-center gap-3 px-6 py-4 font-medium transition-colors duration-200 border-b-2 ${
              activeTab === 'answered'
                ? 'border-purple-500 text-purple-600 bg-purple-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <SafeIcon icon={FiHelpCircle} className="w-5 h-5" />
            Answered Questions
            <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
              {answeredQuestions.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('learnings')}
            className={`flex items-center gap-3 px-6 py-4 font-medium transition-colors duration-200 border-b-2 ${
              activeTab === 'learnings'
                ? 'border-purple-500 text-purple-600 bg-purple-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <SafeIcon icon={FiBookOpen} className="w-5 h-5" />
            Learnings & Tips
            <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
              {learnings.length}
            </span>
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'answered' ? (
            /* Answered Questions Tab */
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              key="answered"
            >
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Answered Questions ({filteredData.length})
                </h2>
                <p className="text-gray-600">Browse questions with verified answers from the community</p>
              </div>
              <div className="space-y-6">
                {filteredData.map((question, index) => (
                  <motion.div
                    key={question.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="border border-gray-200 rounded-lg hover:border-purple-200 hover:shadow-md transition-all duration-200 cursor-pointer"
                    onClick={() => onNavigate('question-details', question.id)}
                  >
                    {/* Question Header */}
                    <div className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 hover:text-purple-600 transition-colors">
                          {question.title}
                        </h3>
                        <div className="flex gap-2 ml-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(question.category)}`}>
                            {question.category}
                          </span>
                          {question.tags && question.tags.length > 0 && (
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTagColor(question.tags[0])}`}>
                              {question.tags[0]}
                            </span>
                          )}
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(question.status)}`}>
                            {question.status}
                          </span>
                        </div>
                      </div>
                      
                      {question.description && (
                        <p className="text-gray-700 mb-4 leading-relaxed">
                          {truncateText(question.description, 150)}
                        </p>
                      )}
                      
                      <div className="text-sm text-gray-600">
                        Asked by <span className="font-medium">{question.asked_by}</span> • {formatDate(question.created_at)}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ) : (
            /* Learnings & Tips Tab */
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              key="learnings"
            >
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Learnings & Tips ({filteredData.length})
                </h2>
                <p className="text-gray-600">Explore knowledge and insights shared by the community</p>
              </div>
              <div className="space-y-6">
                {filteredData.map((learning, index) => (
                  <motion.div
                    key={learning.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="border border-gray-200 rounded-lg p-6 hover:border-green-200 hover:shadow-md transition-all duration-200 cursor-pointer"
                    onClick={() => onNavigate('learning-details', learning.id)}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 hover:text-green-600 transition-colors">
                        {learning.title}
                      </h3>
                      <div className="flex gap-2 ml-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(learning.category)}`}>
                          {learning.category}
                        </span>
                        {learning.tags && learning.tags.length > 0 && (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTagColor(learning.tags[0])}`}>
                            {learning.tags[0]}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
                      <p className="text-blue-700 leading-relaxed text-sm">
                        {truncateText(learning.content)}
                      </p>
                    </div>
                    <div className="flex justify-between items-center text-sm text-gray-600">
                      <div>
                        Shared by <span className="font-medium">{learning.shared_by}</span>
                      </div>
                      <span>{formatDate(learning.created_at)}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* No Results State */}
          {filteredData.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-center py-12"
            >
              <SafeIcon icon={activeTab === 'answered' ? FiHelpCircle : FiBookOpen} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
              <p className="text-gray-600">
                Try adjusting your search terms or filters to find {activeTab === 'answered' ? 'answered questions' : 'learnings'}
              </p>
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default KnowledgeBase;